import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Icon } from './Icon.js';

// Persistent "קבוצות פרסום" setup for a CONNECTED number — NOT a modal you can lose. Driven entirely
// by GET /accounts/:id/groups (live groups + current selections), so a refresh always shows the
// connected account, its real groups, and what's already picked. Toggling a group on/off creates or
// removes its Target; a dropdown designates the sync group. Deselection works (the #1..#3 fixes).

export function GroupSetupPanel({ accountId, onClose }: { accountId: string; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const key = ['groups', accountId];
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.accountGroups(accountId),
    // Poll only while we're still waiting for the live session to hand over the groups.
    refetchInterval: (q) => (q.state.data?.requiresSession ? 2500 : false),
  });

  const groups = data?.groups ?? [];
  const selected = useMemo(() => new Set(data?.selectedJids ?? []), [data?.selectedJids]);
  const syncGroupJid = data?.syncGroupJid ?? null;

  const [suffix, setSuffix] = useState('🔗 הצטרפו לערוץ ←');
  const [syncChoice, setSyncChoice] = useState<string>('');
  const [adminPhone, setAdminPhone] = useState('');
  const [busyJid, setBusyJid] = useState<string | null>(null);

  // Prefill the sync dropdown + admin phone from the persisted binding once data arrives.
  const effectiveSync = syncChoice || syncGroupJid || '';
  const adminHint = useMemo(() => {
    const jid = (data?.adminJids ?? []).find((j) => j.endsWith('@s.whatsapp.net'));
    return jid ? jid.split('@')[0] : '';
  }, [data?.adminJids]);
  const effectiveAdmin = adminPhone || adminHint;

  const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: key }).then(() => qc.invalidateQueries({ queryKey: ['targets'] }));

  const toggleTarget = useMutation({
    mutationFn: async (g: { jid: string; name: string; memberCount: number }) => {
      setBusyJid(g.jid);
      if (selected.has(g.jid)) {
        await api.removeTargetFromGroup({ groupJid: g.jid });
      } else {
        await api.addTargetFromGroup({ waAccountId: accountId, groupJid: g.jid, name: g.name, memberCount: g.memberCount, suffix: suffix.trim() || undefined });
      }
    },
    onSettled: async () => { await invalidate(); setBusyJid(null); },
  });

  const saveSync = useMutation({
    mutationFn: () => api.setSyncGroup(accountId, { groupJid: effectiveSync, adminPhone: effectiveAdmin.trim() || undefined }),
    onSuccess: invalidate,
  });

  const syncGroupName = groups.find((g) => g.jid === syncGroupJid)?.name;

  return (
    <div className="card" style={{ marginTop: 12, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>קבוצות פרסום</div>
        <button className="mini" onClick={onClose}>סגור</button>
      </div>

      <div style={{ fontSize: 12.5, color: 'var(--text-dim)', lineHeight: 1.7, background: 'var(--surface-1)', borderRadius: 'var(--r-sm)', padding: '10px 12px', marginBottom: 14 }}>
        איך זה עובד: המספר שחיברת הוא <b>מספר הבוט</b> ששולח. אתה כותב את ההודעה מ<b>המספר האישי שלך</b> בתוך <b>קבוצת השליטה</b> — והבוט מפיץ אותה אוטומטית ל<b>קבוצות שסימנת</b>, עם קישור בסוף.
      </div>

      {isLoading ? (
        <div style={{ color: 'var(--text-dim)', fontSize: 13, padding: '10px 0' }}>טוען קבוצות…</div>
      ) : data?.requiresSession ? (
        <div style={{ color: 'var(--text-dim)', fontSize: 13, padding: '10px 0' }}>
          מחכה שהמספר יסיים להתחבר ולטעון את הקבוצות… (הדף יתעדכן לבד)
        </div>
      ) : groups.length === 0 ? (
        <div style={{ color: 'var(--text-dim)', fontSize: 13, padding: '10px 0' }}>לא נמצאו קבוצות למספר הזה.</div>
      ) : (
        <>
          {/* Sync group */}
          <div style={{ fontWeight: 600, fontSize: 13.5, margin: '4px 0 2px' }}>קבוצת שליטה</div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 8 }}>הקבוצה שממנה אתה שולח; ההודעה מופצת ממנה לקבוצות שבחרת.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              value={effectiveSync}
              onChange={(e) => setSyncChoice(e.target.value)}
              style={{ flex: 1, minWidth: 200, height: 40, padding: '0 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--hair)', background: 'var(--surface-1)', color: 'var(--text)', fontSize: 13 }}
            >
              <option value="">— בחר קבוצה —</option>
              {groups.map((g) => <option key={g.jid} value={g.jid}>{g.name}</option>)}
            </select>
            <input
              className="ltr" dir="ltr" placeholder="המספר האישי שלך (+972…)" value={effectiveAdmin}
              onChange={(e) => setAdminPhone(e.target.value)}
              style={{ flex: 1, minWidth: 180, height: 40, padding: '0 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--hair)', background: 'var(--surface-1)', color: 'var(--text)', fontSize: 13 }}
            />
            <button className="btn" disabled={!effectiveSync || saveSync.isPending} onClick={() => saveSync.mutate()}>
              {saveSync.isPending ? 'שומר…' : 'שמור קבוצת שליטה'}
            </button>
          </div>
          {syncGroupJid && (
            <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--ok)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="check" /> קבוצת השליטה הנוכחית: <b>{syncGroupName ?? syncGroupJid}</b>
            </div>
          )}

          {/* Target groups */}
          <div style={{ fontWeight: 600, fontSize: 13.5, margin: '18px 0 6px' }}>
            קבוצות לשליחה <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>· סמן או בטל כדי להוסיף/להסיר</span>
          </div>
          <input
            placeholder="טקסט קבוע בסוף כל הודעה (למשל קישור להצטרפות)" value={suffix} onChange={(e) => setSuffix(e.target.value)}
            style={{ width: '100%', height: 38, padding: '0 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--hair)', background: 'var(--surface-1)', color: 'var(--text)', fontSize: 13, marginBottom: 10 }}
          />
          <div style={{ display: 'grid', gap: 6 }}>
            {groups.map((g) => {
              const isSync = g.jid === syncGroupJid;
              const on = selected.has(g.jid);
              return (
                <label
                  key={g.jid}
                  className="card"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: isSync ? 'not-allowed' : 'pointer', opacity: isSync ? 0.5 : 1, borderColor: on ? 'var(--accent-teal)' : 'var(--hair)' }}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={isSync || (toggleTarget.isPending && busyJid === g.jid)}
                    onChange={() => toggleTarget.mutate(g)}
                  />
                  <span style={{ flex: 1 }}>{g.name}{isSync && <span style={{ color: 'var(--text-dim)', fontSize: 12 }}> · קבוצת שליטה</span>}</span>
                  <span className="mono" style={{ fontSize: 12, color: 'var(--text-dim)' }}>{g.memberCount} חברים</span>
                </label>
              );
            })}
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-dim)' }}>
            נבחרו {selected.size} קבוצות. השינויים נשמרים אוטומטית ומופיעים גם אחרי רענון.
          </div>
        </>
      )}
    </div>
  );
}
